import { useEffect } from 'react'
import type { GetServerSidePropsContext } from 'next'
import dynamic from 'next/dynamic'
import { BogoBanner } from '@mc/bogo'
import { DiscountModal } from '@mc/promos'
import { StaticDataProvider } from '@mc/static-data-react'
import { TRACK_PROPS } from '@mc/track-event'
import { TrackingParamsProvider } from '@mc/tracking-context'
import { RegularFooter } from 'components/Footer/RegularFooter'
import GuestPassBanner from 'components/GuestPassBanner'
import { CoreNavigation } from 'components/Navigation/consumer'
import { StickyFooter } from 'components/StickyFooter'
import { InstructorQuoteSection } from 'experiments/pen-7315_v1_cm_instructor_quote'
import { getEligibleInstructorQuote } from 'experiments/pen-7315_v1_cm_instructor_quote/server'
import { trackPen7565BackendEV } from 'experiments/pen-7565_v1_promo_banner_200_classes/server'
import { trackPen7584BackendEV } from 'experiments/pen-7584_v1_promo_banner_all_classes/server'
import { trackPen7680BackendEV } from 'experiments/pen-7680_v1_promo_banner_gift_cta/server'
import { trackPen7694BackendEV } from 'experiments/pen-7694_v1_promo_sticky_footer_gift_cta_routing/server'
import { ssrDynamicHero } from 'libs/dynamic-hero'
import { trackProductViewedCourse } from 'libs/events'
import {
  getLcmCtaOverride,
  type PrimaryCtaOverride,
} from 'libs/lcm/getLcmCtaOverride'
import {
  getCourseMeta,
  getCoursesRelatedToCourse,
  getArticlesRelatedToCourse,
  getFreeTrialByToken,
  getDefaultProduct,
  getCourse,
} from 'libs/server/api'
import {
  getGrowthClassCategories,
  getGrowthClassesBySubcategoryIds,
  getGrowthClassSubcategories,
  getPrimaryCategoryAndSubcategoryForGrowthClass,
  getV2Categories,
  type PrimaryCategoryAndSubcategory,
} from 'libs/server/api/growth'
import { getPageData } from 'libs/server/page-data'
import { pageErrorHandler } from 'libs/server/page-error-handler'
import { transformCourseRedirect } from 'libs/server/redirect'
import { useSprig } from 'libs/sprig'
import { redirect } from 'libs/utils'
import { trackViewedClass } from 'pages/classes/[slug]/events'
import { ClassCategoryBreadcrumbBar } from './components/CategoryBreadcrumbBar'
import { Hero } from './components/Hero'
import Meta from './components/Meta'
import { getEpisodeOrdering } from './utils/episodeOrdering'

const BelowTheFold = dynamic(
  () => import('pages/series/[slug]/components/BelowTheFold'),
  { ssr: false },
)

// T extends a function of some kind, but we don't restrict it by arguments or return type
type FetcherData<T extends (...args: never[]) => unknown> = Awaited<
  ReturnType<T>
>
type Course = FetcherData<typeof getCourse>
type SeriesPageProps = {
  course: Course
  categories: FetcherData<typeof getV2Categories>
  metadata: FetcherData<typeof getCourseMeta>
  trial: FetcherData<typeof getFreeTrialByToken>
  primaryGrowthCatAndSubcat: PrimaryCategoryAndSubcategory
  annualPass: FetcherData<typeof getDefaultProduct>
  relatedArticles: FetcherData<typeof getArticlesRelatedToCourse>
  relatedCourses: FetcherData<typeof getCoursesRelatedToCourse>
  episodeOrdering: ReturnType<typeof getEpisodeOrdering>
  classSubcategories: FetcherData<typeof getGrowthClassSubcategories>
  otherClassesInSameSubcategories: FetcherData<
    typeof getGrowthClassesBySubcategoryIds
  >
  classCategories: FetcherData<typeof getGrowthClassCategories>
  savedDynamicHero: Record<string, string>
  primaryCtaOverride: PrimaryCtaOverride | null
  instructorQuote: ReturnType<typeof getEligibleInstructorQuote> // pen-7315_v1
}

const Series = ({
  course,
  categories,
  metadata,
  trial,
  primaryGrowthCatAndSubcat,
  annualPass,
  relatedArticles,
  relatedCourses,
  episodeOrdering,
  classSubcategories,
  otherClassesInSameSubcategories,
  classCategories,
  savedDynamicHero,
  instructorQuote, // pen-7315_v1
}: SeriesPageProps) => {
  useEffect(() => {
    if (annualPass) {
      trackProductViewedCourse({
        annualPass,
        course,
        coursePrimaryCategory: primaryGrowthCatAndSubcat?.primaryCategory,
        courseCategoryIds: classCategories.map((cat) => cat.id),
        primarySubcategory: primaryGrowthCatAndSubcat?.primarySubcategory,
        courseSubcategoriesIds: classSubcategories.map((subcat) => subcat.id),
      })
    }
  }, [
    course,
    annualPass,
    primaryGrowthCatAndSubcat,
    classSubcategories,
    classCategories,
  ])

  useSprig()

  return (
    <TrackingParamsProvider value={{ [TRACK_PROPS.PAGE_TYPE]: 'next-classes' }}>
      <StaticDataProvider
        value={{
          annualPass,
          course,
          trial,
          metadata,
          primaryGrowthCatAndSubcat,
          categories,
          relatedArticles,
          relatedCourses,
          otherClassesInSameSubcategories,
          classCategories,
          classSubcategories,
          episodeOrdering,
          savedDynamicHero,
          instructorQuote, // pen-7315_v1
        }}
        providerKey={course.slug}
      >
        <Meta />
        <BogoBanner />
        <GuestPassBanner />
        <ClassCategoryBreadcrumbBar />
        <Hero />
        <InstructorQuoteSection /> {/* pen-7315_v1 */}
        <BelowTheFold />
        <StickyFooter />
        <DiscountModal />
      </StaticDataProvider>
    </TrackingParamsProvider>
  )
}

Series.pageSettings = {
  rumLabel: 'Course',
  analyticsLabel: 'Course Marketing',
  showPromo: true,
  navigation: CoreNavigation,
  footer: RegularFooter,
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const {
    query: { slug: querySlug },
    req: {
      cookies: { referral_token: referralToken },
    },
    resolvedUrl,
  } = ctx

  let course: Course
  try {
    course = await getCourse(
      ctx,
      { slug: querySlug as string },
      { withAspectRatios: true }, // PEN-6909: hero reserves nameplate space
    )
  } catch (err) {
    return pageErrorHandler(err)
  }

  const { slug } = course
  if (slug !== querySlug) {
    return redirect(ctx, `/series/${slug}`, { queryExclude: ['slug'] })
  } else if (slug === undefined) {
    return pageErrorHandler(new Error('Slug missing'))
  }

  trackViewedClass(ctx.req, ctx.res, slug)
  await trackPen7565BackendEV(ctx)
  await trackPen7584BackendEV(ctx)
  await trackPen7680BackendEV(ctx)
  await trackPen7694BackendEV(ctx)

  const { savedDynamicHero } = await ssrDynamicHero(ctx, slug)

  const episodeOrdering = getEpisodeOrdering(slug)

  const primaryCtaOverride = getLcmCtaOverride(ctx, slug)

  const classSubcategories = await getGrowthClassSubcategories(ctx, slug)

  const data = await getPageData({
    criticalPromises: {
      metadata: getCourseMeta(ctx, { slug }),
    },
    nonCriticalPromises: {
      annualPass: getDefaultProduct(ctx),
      relatedCourses: getCoursesRelatedToCourse(ctx, { slug }),
      otherClassesInSameSubcategories: getGrowthClassesBySubcategoryIds(
        ctx,
        classSubcategories.map((subcat) => subcat.id),
      ).catch(() => []),
      relatedArticles: getArticlesRelatedToCourse(ctx),
      trial: getFreeTrialByToken(ctx, { referralToken }),
      primaryGrowthCatAndSubcat: getPrimaryCategoryAndSubcategoryForGrowthClass(
        ctx,
        slug,
      ),
      categories: getV2Categories(ctx),
      classCategories: getGrowthClassCategories(ctx, slug),
    },
    propsMapper: (restProps) => ({
      course,
      savedDynamicHero,
      query: ctx.query,
      episodeOrdering,
      primaryCtaOverride,
      classSubcategories,
      instructorQuote: getEligibleInstructorQuote(course), // pen-7315_v1
      ...restProps,
    }),
  })

  return transformCourseRedirect(slug, data, resolvedUrl)
}

export default Series
